import { InvoiceDirection } from "@/apps/tatar/invoiceApp/RAInterfaces";
import {
  ACCostCenter,
  ACInvoiceStructByUnit,
  ACInvoiceStructConfig,
  ACInvoiceType,
  DeclineReasonConfig,
  DeclineReasonConfigWithType,
  InvoiceAccountingRule,
  InvoicePaymentType,
  InvoicePaymentTypeTags,
  ModeOfPayment,
  PaymentKind,
} from "@/apps/tatar/invoiceConfigApp/RACInterfaces";
import i18n from "@/i18n";
import { ObjectId } from "@/model/db/General";
import { BFChooserOption } from "@/modules/abstract-ui/forms/chooser/BFChooser";
import LanguageService from "@/services/LanguageService";
import { InvoiceStructure } from "../../../../apps/AppConfigInterfaces";
import { HTTP } from "../../../../utils/Http";
import { StructType } from "../../../reducers/struct/StructInterface";
import {
  AbstractStructSelectors,
  DataByUnitType,
} from "../AbstractStructSelectors";
import UnitStruct from "./UnitStruct";

export type ModeOfPaymentWithUnit = ModeOfPayment & { unit?: string };
class InvoiceStructClass extends AbstractStructSelectors<ACInvoiceStructByUnit> {
  getStructType(): StructType {
    return "invoice";
  }
  loadStructData(
    units: string[]
  ): Promise<DataByUnitType<ACInvoiceStructByUnit>> {
    return new Promise(async (resolve, reject) => {
      try {
        const data = (await HTTP.get({
          url: `/api/invoice/getInvoiceStruct`,
          target: "EMPTY",
          queryParams: {
            param: {
              types: units,
            },
          },
        })) as InvoiceStructure;

        const result: DataByUnitType<ACInvoiceStructByUnit> = {};
        units.forEach((unit) => {
          result[unit] = {
            invoiceConfig: data.invoiceConfig.find((e) => e.data.type === unit),
            invoiceTypes: data.invoiceTypes.filter((e) => e.data.type === unit),
          };
        });

        resolve(result);
      } catch (err) {
        reject(err);
      }
    });
  }

  getAccountingRule(unit: string, direction: InvoiceDirection) {
    return this.useCache<InvoiceAccountingRule>(
      "getAccountingRule",
      arguments,
      () => {
        const allData = this.getAllData();
        for (const dataOfUnit of allData) {
          if (dataOfUnit.invoiceConfig.data.type === unit) {
            return dataOfUnit.invoiceConfig.data[
              direction === "INCOMING"
                ? "incomingAccountingRule"
                : "outgoingAccountingRule"
            ];
          }
        }

        return null;
      }
    );
  }

  getInvoiceBookingEndDate(entity: ObjectId) {
    return this.useCache<Date>("getInvoiceBookingEndDate", arguments, () => {
      const allData = this.getAllData();

      const allBookingEnd = allData
        .map((e) => e.invoiceConfig.data.bookingEnd)
        .filter(Boolean)
        .flat();
      const bookingEnd = allBookingEnd?.find((e) => e.entity === entity);
      return bookingEnd?.endDate || null;
    });
  }

  getInvoiceCostCenter(id: ObjectId) {
    return this.useCache<ACCostCenter>(
      "getInvoiceCostCenter",
      arguments,
      () => {
        const allCostcenters = this.getAllData()
          .map((e) => e.invoiceTypes)
          .flat()
          .map((e) => e.data.costCenters)
          .flat();

        return allCostcenters.find((e) => e.costCenterId === id);
      }
    );
  }

  getInvoiceConfigByUnit(unit: string): ACInvoiceStructConfig {
    return this.useCache<ACInvoiceStructConfig>(
      "getInvoiceConfigByUnit",
      arguments,
      () => this.getData(unit).invoiceConfig
    );
  }
  getInvoiceTypesByUnit(unit: string): ACInvoiceType[] {
    return this.useCache<ACInvoiceType[]>(
      "getInvoiceTypesByUnit",
      arguments,
      () => this.getData(unit).invoiceTypes
    );
  }

  getInvoiceTypesByUnitAndDirection(
    unit: string,
    direction: InvoiceDirection
  ): ACInvoiceType[] {
    return this.useCache<ACInvoiceType[]>(
      "getInvoiceTypesByUnit",
      arguments,
      () =>
        this.getData(unit).invoiceTypes.filter(
          (e) => e.data.direction === direction
        )
    );
  }
  getInvoiceTypeByUnit(unit: string, id: string): ACInvoiceType {
    return this.useCache<ACInvoiceType>("getInvoiceType", arguments, () =>
      this.getInvoiceTypesByUnit(unit).find((e) => e._id === id)
    );
  }

  getInvoiceType(id: string): ACInvoiceType {
    return this.useCache<ACInvoiceType>("getInvoiceType", arguments, () => {
      const allData = this.getAllData();
      for (const dataOfUnit of allData) {
        for (const invoiceType of dataOfUnit.invoiceTypes) {
          if (invoiceType._id === id) {
            return invoiceType;
          }
        }
      }
    });
  }

  getPaymentKindOptions() {
    return this.useCache<BFChooserOption<string>[]>(
      "getPaymentKindOptions",
      arguments,
      () => {
        return Object.values(PaymentKind).map((val) => ({
          value: val,
          label: i18n.t("ra:Invoice.PaymentKind." + val, val),
        }));
      }
    );
  }

  getPaymentType(id: string): InvoicePaymentType {
    return this.useCache<InvoicePaymentType>(
      "getPaymentType",
      arguments,
      () => {
        const allData = this.getAllData();
        for (const dataOfUnit of allData) {
          for (const paymentType of dataOfUnit?.invoiceConfig?.data
            ?.paymentTypes || []) {
            if (paymentType.id === id) {
              return paymentType;
            }
          }
        }
      }
    );
  }

  getDeclineReason(
    direction: InvoiceDirection,
    id: ObjectId
  ): DeclineReasonConfig & { type: string } {
    return this.useCache<DeclineReasonConfig & { type: string }>(
      "getDeclineReason",
      arguments,
      () => {
        const allData = this.getAllData();
        for (const dataOfUnit of allData) {
          const declineReasons =
            dataOfUnit?.invoiceConfig?.data?.[
              direction === "INCOMING"
                ? "incomingDeclineReasons"
                : "outgoingDeclineReasons"
            ] || [];
          for (const declineReason of declineReasons) {
            if (declineReason.id === id) {
              return {
                ...declineReason,
                type: dataOfUnit.invoiceConfig.data.type,
              };
            }
          }
        }
        return null;
      }
    );
  }
  getDeclineReasons(
    direction: InvoiceDirection,
    units?: string[]
  ): DeclineReasonConfigWithType[] {
    return this.useCache<DeclineReasonConfigWithType[]>(
      "getDeclineReasons",
      arguments,
      () => {
        const allData = this.getAllData();
        const result: DeclineReasonConfigWithType[] = [];
        for (const dataOfUnit of allData) {
          if (units && !units.includes(dataOfUnit.invoiceConfig.data.type)) {
            continue;
          }
          result.push(
            ...(
              dataOfUnit?.invoiceConfig?.data?.[
                direction === "INCOMING"
                  ? "incomingDeclineReasons"
                  : "outgoingDeclineReasons"
              ]?.map((e) => ({
                ...e,
                type: dataOfUnit.invoiceConfig.data.type,
              })) || []
            ).filter((e) => e.status !== "archived")
          );
        }
        return result;
      }
    );
  }
  getDeclineReasonsForChooser(
    direction: InvoiceDirection,
    units?: string[]
  ): BFChooserOption<string>[] {
    return this.useCache<BFChooserOption<string>[]>(
      "getDeclineReasonsForChooser",
      arguments,
      () => {
        return this.getDeclineReasons(direction, units).map((e) => ({
          value: e.id,
          label: LanguageService.translateLabel(e.displayName),
          subLabel: e.info ? LanguageService.translateLabel(e.info) : undefined,
          group:
            units?.length === 1 ? undefined : UnitStruct.getUnitLabel(e.type),
          // subLabel: LanguageService.translateLabel(e.info),
        }));
      }
    );
  }

  getModeOfPayment(
    direction: InvoiceDirection,
    id: string
  ): ModeOfPaymentWithUnit {
    return this.useCache<ModeOfPaymentWithUnit>(
      "getModalOfPayment",
      arguments,
      () => {
        const payments = this.getModalOfPayments(direction);
        return payments.find((e) => e.id === id);
      }
    );
  }
  getModalOfPayments(direction: InvoiceDirection, units?: string[]) {
    return this.useCache<ModeOfPaymentWithUnit[]>(
      "getModalOfPayments",
      arguments,
      () => {
        const allData = this.getAllData();
        const result: ModeOfPaymentWithUnit[] = [];
        for (const dataOfUnit of allData) {
          if (units && !units.includes(dataOfUnit.invoiceConfig.data.type)) {
            continue;
          }
          result.push(
            ...(
              dataOfUnit?.invoiceConfig?.data?.[
                direction === "INCOMING"
                  ? "incomingPaymentMode"
                  : "outgoingPaymentMode"
              ] || []
            )
              .map((e) => ({ ...e, unit: dataOfUnit.invoiceConfig.data.type }))
              .filter((e) => e.status !== "archived")
          );
        }
        return result;
      }
    );
  }
  getRelevantActivitiesFor(invoiceType: string) {
    return this.useCache<string[]>(
      "getRelevantActivitiesFor",
      arguments,
      () => {
        const invoiceTypeData = this.getInvoiceType(invoiceType);
        if (!invoiceTypeData) return [];
        return invoiceTypeData.data.relevantActivity || [];
      }
    );
  }
  getModalOfPaymentsForChooser(direction: InvoiceDirection, units?: string[]) {
    return this.useCache<BFChooserOption<string>[]>(
      "getModalOfPaymentsForChooser",
      arguments,
      () => {
        return this.getModalOfPayments(direction, units).map((e) => ({
          value: e.id,
          label: e.displayName,
          group:
            units?.length === 1 ? undefined : UnitStruct.getUnitLabel(e.unit),
          subLabel: e.identifier,
        }));
      }
    );
  }

  checkPaymentTypeTag(
    paymentTypeId: string,
    tag: InvoicePaymentTypeTags
  ): boolean {
    return this.useCache<boolean>("checkPaymentTypeTag", arguments, () => {
      const paymentType = this.getPaymentType(paymentTypeId);
      return paymentType?.tags?.includes(tag);
    });
  }

  getCustomFields(type: string) {
    return this.useCache<{ id: string; displayName: string }[]>(
      "getCustomFields",
      arguments,
      () => {
        const invoiceConfig = this.getInvoiceConfig(type);
        if (!invoiceConfig) return [];
        return [
          // No fixed custom fields in InvoiceStruct
          ...(invoiceConfig.data.customFields || []).map((e) => ({
            id: e.id,
            displayName: LanguageService.translateLabel(e.displayName),
          })),
        ];
      }
    );
  }

  getInvoiceConfig(type: string) {
    return this.useCache<ACInvoiceStructConfig>(
      "getInvoiceConfig",
      arguments,
      () => {
        const allData = this.getAllData();
        return allData
          .map((e) => e.invoiceConfig)
          .find((e) => e.data.type === type);
      }
    );
  }

  getPaymentTypes(type?: string[]) {
    return this.useCache<(InvoicePaymentType & { type: string })[]>(
      "getPaymentTypes",
      arguments,
      () => {
        const allData = this.getAllData();
        return allData
          .filter((e) =>
            type === undefined ? true : type.includes(e.invoiceConfig.data.type)
          )
          .map((e) =>
            (e.invoiceConfig.data.paymentTypes || []).map((paymentType) => ({
              ...paymentType,
              type: e.invoiceConfig.data.type,
            }))
          )
          .flat();
      }
    );
  }

  getPaymentTypesOf(type: string) {
    return this.useCache<InvoicePaymentType[]>(
      "getPaymentTypesOf",
      arguments,
      () => this.getPaymentTypes([type])
    );
  }

  getInvoiceTypeByRelevantActivity(
    direction: InvoiceDirection,
    activityAssetType: string
  ) {
    return this.useCache<ACInvoiceType[]>(
      "getInvoiceTypeByRelevantActivity",
      arguments,
      () => {
        const allData = this.getAllData();
        return allData
          .map((e) => e.invoiceTypes)
          .flat()
          .filter(
            (e) =>
              e.data.direction === direction &&
              e.data.relevantActivity?.includes(activityAssetType)
          );
      }
    );
  }

  getInvoiceTypeLabel(mainId: string, selectedId: string) {
    return this.useCache<string>("getInvoiceTypeLabel", arguments, () => {
      const mainType = this.getInvoiceType(mainId);
      if (!mainType) return "Unkown";
      if (mainType._id === selectedId) {
        return i18n.t("Global.Labels.misc", "Sonstiges");
      } else {
        return (
          LanguageService.translateLabel(
            mainType.data.costCenters.find((e) => e.costCenterId === selectedId)
              ?.displayName
          ) || "Unkown"
        );
      }
    });
  }

  getInvoiceTypes(direction: InvoiceDirection, type?: string | string[]) {
    return this.useCache<ACInvoiceType[]>("getInvoiceTypes", arguments, () => {
      const allData = this.getAllData();
      return allData
        .map((e) => e.invoiceTypes)
        .flat()
        .filter((e) => e.data.direction === direction)
        .filter((e) => {
          if (type === undefined) return true;
          if (Array.isArray(type)) return type.includes(e.data.type);
          return e.data.type === type;
        });
    });
  }

  generateDocumentName(invoice: any): string {
    let recipient = "recipient";
    let invoiceId = invoice.data.invoice.invoiceId
      .replace(/[^a-zA-Z0-9\s]/gm, "")
      .split(" ")
      .join("");
    return `${invoice.id}_${recipient}_${invoiceId}.pdf`;
  }

  getInvoiceTypeCatalogForMainType(id: string) {
    return this.useCache<
      { value: string; label: string; description?: string }[]
    >("getInvoiceTypeCatalogForMainType", arguments, () => {
      const mainType = this.getInvoiceType(id);
      if (!mainType) {
        return [];
      }
      return [
        ...(mainType.data.costCenters || []).map((e) => ({
          value: e.costCenterId,
          label: LanguageService.translateLabel(e.displayName),
          description: LanguageService.translateLabel(e.description),
        })),
        {
          value: id,
          label: i18n.t("ra:Invoice.InvoiceType.otherSubcategory", "Sonstiges"),
        },
      ];
    });
  }
}
const InvoiceStruct = new InvoiceStructClass();
(window as any).InvoiceStruct = InvoiceStruct;
export default InvoiceStruct;
